/**
 * THE LEDGER — the running commentary, and the audit trail, which are the same
 * thing.
 *
 * Every row is one `RuleEvent` exactly as the engine emitted it: the sentence it
 * wrote, the rulebook page it came from, the dice as they came up, and every
 * modifier named and valued. Nothing is summarised, re-derived or rounded here.
 * A player who wants to know why a card just moved should be able to point at
 * the row and read the rule off it.
 *
 * Selecting a row lights that event's actors on the boards, so the sentence and
 * the cards it moved are never more than a glance apart.
 */

import { useEffect, useRef } from 'react';
import type { DiceRecord, Modifier, RuleEvent } from '../domain/ruleEvent.ts';
import type { RaceCamRead, TurnResult } from '../domain/turn.ts';
import { Cite } from './Cite.tsx';

export interface FeedSelection {
  turn: number;
  index: number;
}

export interface EventFeedProps {
  /** Every turn the engine produced. */
  turns: readonly TurnResult[];
  /** How many of them have been played. */
  cursor: number;
  selected: FeedSelection | null;
  onSelect: (selection: FeedSelection | null, actorIds: readonly string[]) => void;
  /**
   * A driver's name, for the movement strip.
   *
   * Howard, 16 Aug 2026, reading a Big One: *"finding out who got knocked out
   * is hard."* He was reading `12 → out-of-race  30 → out-of-race  32 →
   * out-of-race` — eight men's afternoons ending, printed as starting positions
   * with no names on them. Every id was right there in the event and the strip
   * had never been given anything to turn one into a name.
   */
  nameOf?: (driverId: string) => string;
  /**
   * TELL THIS TURN AS A STORY — D-EZ-55.
   *
   * Howard, 18 Aug 2026: *"a button in the top right where the whole ledger for
   * a turn is read by the ai and then written upon click in an alternate view."*
   *
   * On every turn block rather than once on the panel, because "a turn" is the
   * unit he named and a single button would have had to guess which one he
   * meant. Absent, the buttons do not render — this component is used in places
   * that have no race behind them.
   */
  onTellTurn?: (turn: number) => void;
}

export default function EventFeed({ turns, cursor, selected, onSelect, nameOf, onTellTurn }: EventFeedProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const played = turns.slice(0, cursor);

  // Newest turn sits at the top; jump back to it whenever a turn is run.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
  }, [cursor]);

  const eventCount = played.reduce((n, t) => n + t.events.length, 0);

  return (
    <aside className="feed" aria-label="Race record">
      <div className="feed__head">
        {/* D-EZ-101 — one name for the race log: "Race record". */}
        <h2>Race record</h2>
        <p className="feed__count">
          {eventCount} call{eventCount === 1 ? '' : 's'}
          <Cite> · every one cited</Cite>
        </p>
      </div>

      <div className="feed__scroll" ref={scrollRef} tabIndex={0}>
        {played.length === 0 && (
          <p className="feed__empty">
            Nothing has happened yet. Drop the green and the rulings start landing here.
          </p>
        )}

        {[...played].reverse().map((turn) => (
          <section className="tblock" key={turn.turn} aria-label={`Turn ${turn.turn}`}>
            <header className={`tblock__head tblock__head--${turn.kind}`}>
              <span className="tblock__n">Turn {turn.turn}</span>
              <span className="tblock__kind">{turn.kind}</span>
              {turn.yellowCameOut && (
                <span className="tblock__yellow">
                  caution · {turn.yellowTurnsCalled} turn{turn.yellowTurnsCalled === 1 ? '' : 's'}
                </span>
              )}
              {onTellTurn && (
                /*
                  TOP RIGHT OF THE TURN, which is where Howard asked for it. It
                  reads the whole block below it — every ruling, the dice, the
                  running order either side — and opens the telling over the top.
                  The ledger is untouched underneath and is exactly where it was
                  when the modal closes.
                */
                <button
                  type="button"
                  className="tblock__tell"
                  onClick={() => onTellTurn(turn.turn)}
                  title={`Read all ${turn.events.length} ruling${turn.events.length === 1 ? '' : 's'} of turn ${turn.turn} and tell it as a story`}
                >
                  Tell it
                </button>
              )}
            </header>

            {turn.raceCam && <RaceCamLine cam={turn.raceCam} />}

            <ol className="tblock__events">
              {turn.events.map((event, index) => {
                const isSelected =
                  selected !== null && selected.turn === turn.turn && selected.index === index;
                return (
                  <li key={`${turn.turn}-${index}`}>
                    <button
                      type="button"
                      className={`ev ev--${eventFamily(event)}${isSelected ? ' is-selected' : ''}`}
                      aria-pressed={isSelected}
                      onClick={() =>
                        isSelected
                          ? onSelect(null, [])
                          : onSelect({ turn: turn.turn, index }, event.actorIds)
                      }
                    >
                      <span className="ev__tag">{event.momentTag}</span>
                      <span className="ev__desc">{event.description}</span>
                      <Cite className="ev__cite">{event.ruleCitation}</Cite>

                      {event.dice.length > 0 && (
                        <span className="ev__dice">
                          {event.dice.map((roll, i) => (
                            <DiceRow key={i} roll={roll} />
                          ))}
                        </span>
                      )}

                      {event.modifiers.length > 0 && (
                        <span className="ev__mods">
                          {event.modifiers.map((m, i) => (
                            <ModifierPill key={i} modifier={m} expanded={isSelected} />
                          ))}
                        </span>
                      )}

                      {event.positionDeltas.length > 0 && (
                        <span className="ev__moves">
                          {event.positionDeltas.map((d, i) => {
                            const who = nameOf?.(d.driverId);
                            const gone = d.to === null;
                            return (
                              <span className={`mv${gone ? ' mv--out' : ''}`} key={i}>
                                {who && <span className="mv__who">{who}</span>}
                                <b>{d.from}</b>
                                <i aria-hidden="true">→</i>
                                <b>{gone ? (d.placement ?? 'out') : d.to}</b>
                              </span>
                            );
                          })}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </aside>
  );
}

/** The RACE CAM read that opened the turn, printed the way the track card reads. */
function RaceCamLine({ cam }: { cam: RaceCamRead }) {
  return (
    <p className="cam">
      <span className="cam__label">Race cam</span>
      <span className="die die--black">{cam.black}</span>
      <span className="cam__group">{cam.group}</span>
      <span className="die die--metallic">{cam.metallic[0]}</span>
      <span className="die die--metallic">{cam.metallic[1]}</span>
      <span className="cam__total">= {cam.total}</span>
      <span className="cam__result">{cam.printed}</span>
      {cam.doubles && <span className="cam__flag">doubles · multiple events</span>}
      {cam.overridden && <span className="cam__flag">footnote: {cam.result}</span>}
      {cam.footnote && <span className="cam__note">{cam.footnote}</span>}
    </p>
  );
}

function ModifierPill({ modifier, expanded }: { modifier: Modifier; expanded: boolean }) {
  const detail = modifier.note ?? modifier.detail;
  const signed =
    typeof modifier.value === 'number' && modifier.value > 0 ? `+${modifier.value}` : modifier.value;
  return (
    <span className="mod" title={detail}>
      <span className="mod__name">{modifier.name}</span>
      <span className="mod__val">{signed}</span>
      {expanded && detail && <span className="mod__note">{detail}</span>}
    </span>
  );
}

/**
 * One roll, as rolled. The die colours follow the physical game: red / white /
 * blue for a challenge, black for the RACE CAM group die, metallic for the pair.
 */
function DiceRow({ roll }: { roll: DiceRecord }) {
  const roles = dieRoles(roll);
  return (
    <span className="roll">
      {roll.label && <span className="roll__label">{roll.label}</span>}
      <span className="roll__dice">
        {roll.dice.map((face, i) => (
          <span key={i} className={`die die--${roles[i] ?? 'metallic'}`}>
            {face}
          </span>
        ))}
        {roll.total !== undefined && <span className="roll__read">= {roll.total}</span>}
        {roll.code !== undefined && <span className="roll__read">{roll.code}</span>}
        {/*
          D-018 — WHOSE DICE THESE WERE, on the row itself.

          "A hand-entered 6 that looks identical to a rolled 6 destroys that."
          Absent on a seeded roll, so the ledger of every race played before
          manual entry existed reads exactly as it always did. `mixed` is the
          one turn that straddles a hand→engine hand-over and it says so rather
          than picking a side — see `DiceRecord.entry`.
        */}
        {roll.entry && (
          <span className="roll__entry" title={
            roll.entry === 'hand'
              ? 'You rolled these. Hand-entered at the table.'
              : 'This turn straddled the hand-over from your dice to the game.'
          }>
            {roll.entry === 'hand' ? 'your dice' : 'yours, then the game'}
          </span>
        )}
      </span>
      {roll.flags &&
        Object.entries(roll.flags)
          .filter(([, on]) => on)
          .map(([name]) => (
            <span className="roll__flag" key={name}>
              {name}
            </span>
          ))}
    </span>
  );
}

const FACE_ROLE: Record<string, string> = {
  red: 'red',
  white: 'white',
  blue: 'blue',
  black: 'black',
  boxA: 'metallic',
  boxB: 'metallic',
  oddEven: 'metallic',
};

/**
 * Which physical die each face belongs to, taken from the record's own `faces`
 * map where the engine supplied one. The 11-66 charts are printed
 * "[red and white dice]" on the boards, so a code read gets those two.
 */
export function dieRoles(roll: DiceRecord): string[] {
  if (roll.faces) {
    const names = Object.keys(roll.faces);
    if (names.length === roll.dice.length) return names.map((n) => FACE_ROLE[n] ?? 'metallic');
  }
  if (roll.kind === '11-66') return ['red', 'white'];
  if (roll.kind === 'd6') return ['black'];
  return roll.dice.map(() => 'metallic');
}

/** Coarse family used only to colour-code a row. */
export function eventFamily(event: RuleEvent): string {
  const t = event.eventType;
  if (t.startsWith('quality-challenge')) return 'challenge';
  if (t === 'duel' || t === 'duel-call') return 'duel';
  if (t === 'trouble' || t === 'problem' || t === 'rare') return 'incident';
  if (t.startsWith('pit') || t === 'fast-pit') return 'pit';
  if (t === 'feud-fume') return 'temper';
  if (t === 'yellow-flag-turn') return 'yellow';
  if (t === 'race-cam') return 'cam';
  return 'plain';
}
